import {
  ItemRequest,
  WorkflowAction,
  WorkflowOperator,
  WorkflowTrigger,
} from '@/api';

/** @transient */
export enum UIWorkflowConditionType {
  DATE = 'date',
  SCHEDULE = 'schedule',
  FIELD_VALUE = 'field_value',
  FIELD_CHANGE = 'field_change',
}

/** @transient */
export type UIWorkflowCondition = {
  type: UIWorkflowConditionType;
  operator: WorkflowOperator;
  label: string;
  value?: string | null;
};

/** Primitive types allowed in workflow condition values */
type WorkflowConditionPrimitive = string | number | boolean | null;

/** @transient */
export type WorkflowUIConditionProperties = {
  field: string;
  operator: WorkflowOperator;
  value:
    | Array<WorkflowConditionPrimitive | Array<WorkflowConditionPrimitive>>
    | WorkflowConditionPrimitive;
};

/** @transient */
export type UIItemWorkflowTriggerCondition = {
  field:
    | 'parentNumber'
    | 'hierarchy'
    | 'color'
    | 'priorityId'
    | 'authorUsername'
    | 'labels'
    | 'estimate'
    | 'pointEffort'
    | 'laneId'
    | 'teamId'
    | 'boardId'
    | 'boardType'
    | 'assigneeUsername'
    | 'customFields'
    | 'dueDate'
    | 'workTypeId'
    | 'timeSpentSeconds';
  label:
    | 'Direct Parent'
    | 'Parent Hierarchy'
    | 'Color'
    | 'Priority'
    | 'Author'
    | 'Tags'
    | 'Time Estimate'
    | 'Effort Points'
    | 'Status'
    | 'Team'
    | 'Board'
    | 'Cycle'
    | 'Assignee'
    | 'Custom Field'
    | 'Due Date'
    | 'Item Type'
    | 'Time Spent';
  icon: string;
  iconComponent: 'octicon' | 'custom-icon' | 'fluent-icon';
  types: Array<UIWorkflowCondition>;
};

/** @transient */
export type WorkflowContextItem = {
  field: string;
  type: string;
  label: string;
};

/** @transient */
export type ActionBoardFormRelativeValue = {
  type: 'board';
  boardNumber?: number; // futurePlus
  boardFolderId?: string;
};

/** @transient */
export type ActionBoardFormSpecificValue = {
  type: 'value';
  selectedBoardId?: string | null;
};

/** @transient */
export type UIAutomationTrigger = {
  type: WorkflowTrigger;
  field?: string;
  label:
    | 'Every Time Period'
    | 'Due Date Arrived'
    | 'Work Item Created'
    | 'Work Item Deleted'
    | 'Item Type Changed'
    | 'Item Parent Changed'
    | 'Item Color Changed'
    | 'Status Changed'
    | 'Priority Changed'
    | 'Team Updated'
    | 'Assignee Updated'
    | 'Point Effort Updated'
    | 'Time Estimate Updated'
    | 'Cycle/Board Updated'
    | 'Custom Field Updated'
    | 'Tags Updated'
    | 'Due Date Changed'
    | 'Workstream Created'
    | 'Workstream Deleted'
    | 'Workstream Parent Changed'
    | 'Workstream Color Changed'
    | 'Time Spent Updated'
    // Item Request
    | 'Item Request Submitted'
    // Github
    | 'Pull Request Opened'
    | 'Branch Created'
    | 'Commit Created'
    | 'All Checks Passed'
    | 'Check Failed'
    | 'Review Requested'
    | 'Changes Requested'
    | 'All Reviews Approved'
    | 'All Pull Requests Merged/Closed';
  icon: string;
  iconComponent: 'octicon' | 'custom-icon' | 'fluent-icon';
  workflowTrigger: WorkflowTrigger;
  possibleConditions: Array<UIItemWorkflowTriggerCondition>;
  context: Array<WorkflowContextItem>;
  minConditions: number;
  maxConditions: number;
  operator?: WorkflowOperator;
};

/** @transient */
export type UIWorkflowTriggerGroup = {
  label: string;
  icon?: string;
  iconComponent?: 'octicon' | 'fluent-icon' | 'custom-icon';
  triggers: Array<UIAutomationTrigger>;
};

/** @transient */
export type TriggerTimeFrequencyInfo = {
  frequency: WorkflowTimeFrequency;
  interval: number;
  hour: number;
  weekdays?: Array<number>;
  monthDays?: Array<number>;
};

/** @transient */
export type TriggerDueDateArrivedInfo = {
  hourOfDay: number;
  interval: number;
  dateMethod?: 'days' | 'weeks' | 'months';
  when: 'before' | 'after';
};

/** @transient */
export type DueDateArrivedInputParams = {
  hourOfDay?: number;
  daysBefore?: number;
  daysAfter?: number;
  ui?: {
    dueDateArrivedFrequency?: 'days' | 'weeks' | 'months';
  };
};

/** @transient */
export type TriggerItemRequestSubmittedInfo = {
  itemRequestIds?: Array<ItemRequest['id']>;
};

/** @transient */
export type UIWorkflowAction = {
  type: WorkflowAction['type'];
  label:
    | 'Send Notification'
    | 'Call Webhook'
    | 'Create Work Item'
    | 'Change Item Parent'
    | 'Change Item Color'
    | 'Change Status'
    | 'Change Priority'
    | 'Change Item Type'
    | 'Change Team'
    | 'Change Assignee'
    | 'Change Point Effort'
    | 'Change Time Estimate'
    | 'Change Time Spent'
    | 'Change Cycle/Board'
    | 'Change Custom Field'
    | 'Add Tags'
    | 'Remove Tags'
    | 'Set Due Date'
    | 'Create Workstream'
    | 'Change Workstream Parent'
    | 'Change Workstream Color'
    | 'Create Subtask'
    | 'Create Relationships';
  icon: string;
  iconComponent: 'octicon' | 'custom-icon' | 'fluent-icon';
  allowedWorkflowTriggers?: Array<WorkflowTrigger>;
  context?: Array<WorkflowContextItem>;
};

/** @transient */
export type UIWorkflowActionGroup = {
  label: string;
  icon?: string;
  iconComponent?: 'octicon' | 'fluent-icon' | 'custom-icon';
  actions: Array<UIWorkflowAction>;
};

/** @transient */
export enum WorkflowTimeFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

/** @transient */
export enum WorkflowCycleOptions {
  CURRENT = 'current',
  FUTURE = 'future',
  PAST = 'past',
}

/** @transient */
export type WorkflowTriggerFilterOption = {
  type: UIAutomationTrigger['type'];
  label: UIAutomationTrigger['label'];
};

/** @transient */
export type WorkflowConditionFieldFilterOption = {
  field: UIItemWorkflowTriggerCondition['field'];
  label: UIItemWorkflowTriggerCondition['label'];
};

/** @transient */
export type WorkflowActionFilterOption = {
  type: UIWorkflowAction['type'];
  label: UIWorkflowAction['label'];
};

/** @transient */
export type WorkflowFilterOptions = {
  q?: string;
  triggers?: Array<WorkflowTriggerFilterOption>;
  conditionFields?: Array<WorkflowConditionFieldFilterOption>;
  actionTypes?: Array<WorkflowActionFilterOption>;
  status?: Array<'active' | 'disabled'>;
  createdByIds?: Array<string>;
};
